import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account';
import { filterBoolean } from '@recruitee/common';
import type { Company } from '@recruitee/company-types';
import { OfferStatus } from '@recruitee/offers-types';
import type { PricingFeatureSubscription, SubscriptionStatus } from '@recruitee/subscription-types';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  public isTrial$: Observable<boolean>;

  private get company(): Company {
    return this.currentCompany || <Company>{};
  }
  private currentCompany: Company;
  private currentCompany$: Observable<Company> = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(filterBoolean);

  constructor(private store: Store) {
    this.isTrial$ = this.currentCompany$.pipe(
      map(company => {
        const { subscription, status, trialPeriodEnd } = company;
        return (
          (!subscription || subscription.status === 'trialing') &&
          status === 'active' &&
          !!trialPeriodEnd
        );
      }),
    );
    this.currentCompany$.subscribe(currentCompany => (this.currentCompany = currentCompany));
  }

  public isStatusChangeAllowed(from: OfferStatus, to: OfferStatus): boolean {
    if (
      [OfferStatus.Draft, OfferStatus.Archived].includes(from) &&
      [OfferStatus.Published, OfferStatus.Internal, OfferStatus.Closed].includes(to)
    )
      return !this.limitExceeded();
    else return true;
  }

  public limitExceeded(): boolean {
    const { usedJobs } = this.company;
    return this.hasSlotsLimit() && Boolean(this.company.subscription && usedJobs >= this.limit());
  }

  public wouldCountExceedLimit(count: number): boolean {
    const { usedJobs } = this.company;
    return (
      this.hasSlotsLimit() && Boolean(this.company.subscription && usedJobs + count > this.limit())
    );
  }

  public hasSlotsLimit(): boolean {
    return !this.company.subscription?.unlimited;
  }

  public isCanceled(): boolean {
    return this.hasStatus(['non_renewing', 'canceled'], false);
  }

  public isExpired(): boolean {
    return this.hasStatus(['expired', 'blocked'], false);
  }

  public isPastDue(): boolean {
    const { subscription } = this.company;

    return this.hasStatus('past_due') || (subscription != null && subscription.pastDue);
  }

  public isPaused(): boolean {
    return this.hasStatus('paused');
  }

  public isFuture(): boolean {
    return this.hasStatus('future');
  }

  public isInactive(): boolean {
    return this.hasStatus('inactive');
  }

  public isTrialing(): boolean {
    return this.hasStatus('trialing');
  }

  public isExpiredExplicit(): boolean {
    return this.hasStatus('expired');
  }

  public isBlocked(): boolean {
    return this.hasStatus('blocked');
  }

  public isNonRenewing(): boolean {
    return this.hasStatus('non_renewing');
  }

  public isTrialEnded(): boolean {
    return ['expired', 'closed'].includes(this.company.status);
  }

  public isTrialEndedWithoutSubscription(): boolean {
    const { status, subscription, trialPeriodEnd } = this.company;
    const isSubscriptionActive = subscription?.active;
    return !isSubscriptionActive && !!trialPeriodEnd && ['expired', 'closed'].includes(status);
  }

  // TODO: (pricing) remove 'isManual' after migration
  public isManual(): boolean {
    return !this.company.subscription?.autoCollection;
  }

  public getBlockAt(): string {
    const {
      company: { subscription: { blockAt = '' } = {} },
    } = <any>this;
    return blockAt;
  }

  public limit(): number {
    const getLimit = () => {
      const sub = <PricingFeatureSubscription>this.company.subscription;
      return sub.slots;
    };

    const limit = getLimit();

    if (limit === null) {
      return Number.POSITIVE_INFINITY;
    }

    return limit;
  }

  public isTrial(): boolean {
    const { subscription, status, trialPeriodEnd } = this.company;
    return (
      (!subscription || subscription.status === 'trialing') &&
      status === 'active' &&
      !!trialPeriodEnd
    );
  }

  private hasStatus(status: SubscriptionStatus | SubscriptionStatus[], strict = true): boolean {
    const { subscription } = this.company;

    if (!subscription) return false;

    const match = (s: string) => s === subscription.status;

    if (Array.isArray(status)) {
      return strict ? status.every(match) : status.some(match);
    } else {
      return match(status);
    }
  }
}
